/**
 * Extração de embedding facial NO NAVEGADOR (on-device) — a foto nunca sai do
 * dispositivo; só o vetor de 128 floats trafega. Usa @vladmandic/face-api
 * (TF.js): tinyFaceDetector (detecção) + faceLandmark68 (alinhamento) +
 * faceRecognitionNet (descritor 128-d, L2-normalizado).
 *
 * O descritor é compatível com o núcleo golden MatcherFacial do backend
 * (distância de cosseno). Como o vetor é L2-normalizado, cosseno e euclidiano
 * são monotônicos: o limiar euclidiano 0.6 recomendado do face-api equivale a
 * uma distância de cosseno ≈ 0.20 (cos = eucl²/2) — o default semeado na config.
 *
 * O módulo face-api (+TF.js, ~1 MB de JS) é importado DINAMICAMENTE, só quando
 * a câmera é usada — não entra no bundle inicial do app.
 */

/** Versão do modelo — deve casar com config.IModeloFacialVersaoAtual do backend. */
export const MODELO_VERSAO = 1;
export const DIMENSOES = 128;

const MODELS_URI = "/models";

// Tipo do namespace face-api sem forçar bundle estático.
type FaceApi = typeof import("@vladmandic/face-api");

let faceapiPromise: Promise<FaceApi> | null = null;
let carregando: Promise<FaceApi> | null = null;

function importarFaceApi(): Promise<FaceApi> {
  if (!faceapiPromise) faceapiPromise = import("@vladmandic/face-api");
  return faceapiPromise;
}

/** Carrega a lib + os pesos uma única vez (lazy). Idempotente e seguro para concorrência. */
export function carregarModelos(): Promise<FaceApi> {
  if (!carregando) {
    carregando = (async () => {
      const faceapi = await importarFaceApi();
      await faceapi.nets.tinyFaceDetector.loadFromUri(MODELS_URI);
      await faceapi.nets.faceLandmark68Net.loadFromUri(MODELS_URI);
      await faceapi.nets.faceRecognitionNet.loadFromUri(MODELS_URI);
      return faceapi;
    })().catch((e) => {
      carregando = null; // permite nova tentativa se falhar o download
      throw e;
    });
  }
  return carregando;
}

export interface ResultadoExtracao {
  embedding: number[];      // 128 floats (descritor L2-normalizado)
  qualidade: number;        // score de detecção 0..1
}

export type ErroExtracao = "sem_face" | "multiplas_faces" | "modelo";

export class FalhaExtracao extends Error {
  constructor(public readonly tipo: ErroExtracao, mensagem: string) {
    super(mensagem);
    this.name = "FalhaExtracao";
  }
}

/**
 * Extrai o descritor facial de um elemento de vídeo/imagem/canvas.
 * Exige exatamente UMA face no quadro (rejeita 0 ou 2+, evitando captura ambígua).
 */
export async function extrairDescritor(
  fonte: HTMLVideoElement | HTMLImageElement | HTMLCanvasElement,
): Promise<ResultadoExtracao> {
  const faceapi = await carregarModelos();

  const detectorOpts = new faceapi.TinyFaceDetectorOptions({ inputSize: 320, scoreThreshold: 0.5 });
  const deteccoes = await faceapi
    .detectAllFaces(fonte, detectorOpts)
    .withFaceLandmarks()
    .withFaceDescriptors();

  if (deteccoes.length === 0)
    throw new FalhaExtracao("sem_face", "Nenhum rosto detectado. Enquadre o rosto na câmera.");
  if (deteccoes.length > 1)
    throw new FalhaExtracao("multiplas_faces", "Mais de um rosto no quadro. Apenas uma pessoa por vez.");

  const d = deteccoes[0];
  return {
    embedding: Array.from(d.descriptor),
    qualidade: Number(d.detection.score.toFixed(4)),
  };
}
